/**
 * Cascading variant selectors + Storefront API fetch for products with 250+ variants
 * or incomplete option matrices. Only activates when needed (see init).
 */
(function () {
  var CASCADE_ALWAYS_VISIBLE = ["Custom", "Further Options"];

  function normalizeVariantOptions(variants, optionCount) {
    return variants.map(function (variant) {
      if (variant.options && variant.options.length) {
        return variant;
      }
      var options = [];
      for (var i = 1; i <= optionCount; i++) {
        options.push(variant["option" + i] || null);
      }
      return Object.assign({}, variant, { options: options });
    });
  }

  function parseEmbeddedVariants(variantSelects) {
    var jsonEl = variantSelects.querySelector('script[type="application/json"]');
    if (!jsonEl) return [];
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (e) {
      return [];
    }
  }

  function replaceVariantData(variantSelects, variants) {
    var jsonEl = variantSelects.querySelector('script[type="application/json"]');
    if (jsonEl) {
      jsonEl.textContent = JSON.stringify(variants);
    }
    variantSelects.variantData = variants;
  }

  function gidToNumericId(gid) {
    if (!gid) return null;
    var parts = String(gid).split("/");
    return parseInt(parts[parts.length - 1], 10);
  }

  function mapStorefrontVariant(node, optionNames) {
    var optionsByName = {};
    (node.selectedOptions || []).forEach(function (so) {
      optionsByName[so.name] = so.value;
    });
    var options = optionNames.map(function (name) {
      return optionsByName[name] || "";
    });

    return {
      id: gidToNumericId(node.id),
      available: !!node.availableForSale,
      options: options,
      option1: options[0] || null,
      option2: options[1] || null,
      option3: options[2] || null,
      featured_image: node.image ? { src: node.image.url } : null,
      price: node.price
        ? Math.round(parseFloat(node.price.amount, 10) * 100)
        : 0,
      title: options.filter(Boolean).join(" / "),
      name: options.filter(Boolean).join(" / "),
    };
  }

  var VARIANTS_QUERY =
    "query getVariants($handle: String!, $cursor: String) {" +
    "  product(handle: $handle) {" +
    "    variants(first: 250, after: $cursor) {" +
    "      pageInfo { hasNextPage endCursor }" +
    "      edges {" +
    "        node {" +
    "          id" +
    "          availableForSale" +
    "          selectedOptions { name value }" +
    "          price { amount currencyCode }" +
    "          image { url }" +
    "        }" +
    "      }" +
    "    }" +
    "  }" +
    "}";

  function fetchAllVariantsFromStorefront(config) {
    if (!config.storefrontAccessToken) {
      console.warn(
        "[variant-cascade] Storefront API token missing; cannot fetch variants beyond 250."
      );
      return Promise.resolve(null);
    }

    var endpoint =
      "https://" +
      config.shopDomain +
      "/api/2024-01/graphql.json";
    var all = [];
    var cursor = null;

    function page() {
      return fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": config.storefrontAccessToken,
        },
        body: JSON.stringify({
          query: VARIANTS_QUERY,
          variables: { handle: config.handle, cursor: cursor },
        }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Storefront API error: " + res.status);
          return res.json();
        })
        .then(function (payload) {
          if (payload.errors && payload.errors.length) {
            throw new Error(payload.errors[0].message || "GraphQL error");
          }
          var connection = payload.data && payload.data.product
            ? payload.data.product.variants
            : null;
          if (!connection) throw new Error("No variant data returned");

          connection.edges.forEach(function (edge) {
            all.push(mapStorefrontVariant(edge.node, config.optionNames));
          });

          if (connection.pageInfo.hasNextPage) {
            cursor = connection.pageInfo.endCursor;
            return page();
          }
          return all;
        });
    }

    return page();
  }

  function setLoading(variantSelects, active) {
    var el = variantSelects.querySelector("[data-variant-cascade-loading]");
    if (el) {
      el.classList.toggle("hidden", !active);
    }
    variantSelects.classList.toggle("variant-cascade-loading-active", active);
  }

  function getSelectForOption(variantSelects, optionIndex, optionName) {
    return (
      variantSelects.querySelector(
        'select[data-option-index="' + optionIndex + '"]'
      ) ||
      variantSelects.querySelector(
        'select[name="options[' + optionName + ']"]'
      )
    );
  }

  function getDropdownForOption(variantSelects, optionIndex) {
    return variantSelects.querySelector(
      '[data-size-dropdown][data-option-index="' + optionIndex + '"]'
    );
  }

  function getSelectedValues(variantSelects, config) {
    return config.optionNames.map(function (name, idx) {
      var select = getSelectForOption(variantSelects, idx, name);
      if (!select || !select.value) return null;
      return String(select.value).trim();
    });
  }

  function resetOptionUI(variantSelects, config, optionIndex) {
    var optionName = config.optionNames[optionIndex];
    var dropdown = getDropdownForOption(variantSelects, optionIndex);
    var select = getSelectForOption(variantSelects, optionIndex, optionName);

    if (dropdown) {
      dropdown.querySelectorAll("[data-size-option]").forEach(function (radio) {
        radio.checked = false;
      });
      var label = dropdown.querySelector("[data-selected-size-label]");
      if (label) {
        label.innerHTML =
          optionName +
          ' <span class="opacity-30">Make selection</span>';
      }
    }

    if (select) {
      var placeholder = select.querySelector('option[value=""]');
      if (placeholder) {
        select.value = "";
      } else {
        select.selectedIndex = 0;
      }
    }
  }

  function setChoiceVisible(choiceEl, visible) {
    if (!choiceEl) return;
    choiceEl.style.display = visible ? "" : "none";
    if (visible && choiceEl.classList) {
      choiceEl.classList.add("text-black");
      choiceEl.classList.remove("text-gray-400", "ss-disabled");
    }
  }

  function isValueAllowed(val, allowedValues) {
    var trimmed = String(val).trim();
    return (
      allowedValues.has(trimmed) ||
      CASCADE_ALWAYS_VISIBLE.indexOf(trimmed) !== -1
    );
  }

  function getSwatchOptionIndex(variantSelects) {
    var swatchRoot = variantSelects.querySelector("#variant-swatches");
    if (!swatchRoot) return -1;
    var idx = swatchRoot.getAttribute("data-swatch-option-index");
    return idx != null ? parseInt(idx, 10) : -1;
  }

  function updateSwatchVisibility(variantSelects, optionIndex, allowedValues) {
    var swatchRoot = variantSelects.querySelector("#variant-swatches");
    if (!swatchRoot) return;
    var swatchOptionIndex = getSwatchOptionIndex(variantSelects);
    if (swatchOptionIndex !== optionIndex) return;

    swatchRoot.querySelectorAll(".swatch-item").forEach(function (btn) {
      var val = btn.getAttribute("data-name");
      if (!val) return;
      setChoiceVisible(btn, isValueAllowed(val, allowedValues));
    });
  }

  function updateOptionVisibility(
    variantSelects,
    config,
    optionIndex,
    allowedValues
  ) {
    var optionName = config.optionNames[optionIndex];
    var dropdown = getDropdownForOption(variantSelects, optionIndex);
    var select = getSelectForOption(variantSelects, optionIndex, optionName);

    updateSwatchVisibility(variantSelects, optionIndex, allowedValues);

    if (dropdown) {
      dropdown.querySelectorAll("[data-size-option]").forEach(function (radio) {
        var label = radio.closest("label");
        var val = String(radio.value).trim();
        var show = isValueAllowed(val, allowedValues);
        setChoiceVisible(label, show);
        if (!show && radio.checked) {
          radio.checked = false;
        }
      });
    }

    if (select) {
      Array.from(select.options).forEach(function (opt) {
        if (!opt.value || opt.disabled) return;
        var show = isValueAllowed(opt.value, allowedValues);
        opt.style.display = show ? "" : "none";
      });
      if (select.value && !isValueAllowed(select.value, allowedValues)) {
        select.value = "";
      }
    }
  }

  function syncSelectsFromRadios(variantSelects, config) {
    config.optionNames.forEach(function (name, idx) {
      var dropdown = getDropdownForOption(variantSelects, idx);
      var select = getSelectForOption(variantSelects, idx, name);
      if (!select) return;
      if (dropdown) {
        var checked = dropdown.querySelector("[data-size-option]:checked");
        if (checked && checked.value) {
          select.value = checked.value;
        }
      }
    });

    var swatchRoot = variantSelects.querySelector("#variant-swatches");
    var swatchIdx = getSwatchOptionIndex(variantSelects);
    if (swatchRoot && swatchIdx >= 0) {
      var activeSwatch = swatchRoot.querySelector(
        ".swatch-item.border-black, .swatch-item.swatch-active"
      );
      var swatchSelect = getSelectForOption(
        variantSelects,
        swatchIdx,
        config.optionNames[swatchIdx]
      );
      if (activeSwatch && swatchSelect && activeSwatch.getAttribute("data-name")) {
        swatchSelect.value = activeSwatch.getAttribute("data-name");
      }
    }
  }

  function buildAllowedValues(allVariants, selected, optionIndex) {
    for (var i = 0; i < optionIndex; i++) {
      if (!selected[i]) {
        return new Set();
      }
    }

    var pool = allVariants;
    for (var j = 0; j < optionIndex; j++) {
      var sel = selected[j];
      pool = pool.filter(function (v) {
        return String(v.options[j]).trim() === String(sel).trim();
      });
    }

    var allowed = new Set();
    pool.forEach(function (v) {
      if (v.options[optionIndex]) {
        allowed.add(String(v.options[optionIndex]).trim());
      }
    });
    return allowed;
  }

  function normalizeSelectedLabelStyles(variantSelects) {
    variantSelects
      .querySelectorAll("[data-selected-size-label] span")
      .forEach(function (span) {
        var text = span.textContent.trim();
        var isPlaceholder =
          span.classList.contains("is-placeholder") ||
          text === "Make selection" ||
          text === "Select add on";
        if (!isPlaceholder && text) {
          span.classList.remove("opacity-30");
          span.classList.add("text-black", "selected-option-value");
        }
      });
  }

  function applyCascadeFilters(variantSelects, allVariants, config) {
    var selected = getSelectedValues(variantSelects, config);
    var optionCount = config.optionNames.length;

    for (var optionIndex = 0; optionIndex < optionCount; optionIndex++) {
      var allowed = buildAllowedValues(allVariants, selected, optionIndex);
      updateOptionVisibility(
        variantSelects,
        config,
        optionIndex,
        allowed
      );
    }

    normalizeSelectedLabelStyles(variantSelects);
  }

  function hookVariantSwatches(variantSelects, config) {
    var swatchRoot = variantSelects.querySelector("#variant-swatches");
    if (!swatchRoot) return;

    var swatchOptionIndex = getSwatchOptionIndex(variantSelects);
    if (swatchOptionIndex < 0) return;

    swatchRoot.addEventListener("click", function (e) {
      var btn = e.target.closest(".swatch-item");
      if (!btn || btn.style.display === "none") return;

      var optionName = config.optionNames[swatchOptionIndex];
      var select = getSelectForOption(
        variantSelects,
        swatchOptionIndex,
        optionName
      );
      var val = btn.getAttribute("data-name");
      if (!select || !val) return;

      select.value = val;
      select.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  function activateCascading(variantSelects, allVariants, config) {
    var optionCount = config.optionNames.length;
    syncSelectsFromRadios(variantSelects, config);
    var lastSelected = getSelectedValues(variantSelects, config).slice();

    hookVariantSwatches(variantSelects, config);
    applyCascadeFilters(variantSelects, allVariants, config);

    variantSelects.addEventListener("change", function (e) {
      var select = e.target.closest("select");
      if (!select || !variantSelects.contains(select)) return;

      var changedIndex = -1;
      config.optionNames.forEach(function (name, idx) {
        if (select.name === "options[" + name + "]") {
          changedIndex = idx;
        }
      });
      if (changedIndex < 0) {
        var idxAttr = select.getAttribute("data-option-index");
        if (idxAttr != null) changedIndex = parseInt(idxAttr, 10);
      }
      if (changedIndex < 0) return;

      var nowSelected = getSelectedValues(variantSelects, config);
      var parentChanged =
        String(nowSelected[changedIndex] || "") !==
        String(lastSelected[changedIndex] || "");

      if (parentChanged) {
        for (var i = changedIndex + 1; i < optionCount; i++) {
          resetOptionUI(variantSelects, config, i);
        }
      }

      lastSelected = getSelectedValues(variantSelects, config);
      applyCascadeFilters(variantSelects, allVariants, config);

      // VariantSelects may re-style labels after this handler — re-apply black text
      setTimeout(function () {
        normalizeSelectedLabelStyles(variantSelects);
      }, 0);
    });
  }

  function hookSelectedLabelStyles(variantSelects) {
    if (variantSelects.dataset.labelStylesHooked === "true") return;
    variantSelects.dataset.labelStylesHooked = "true";
    document.addEventListener("variant:change", function () {
      normalizeSelectedLabelStyles(variantSelects);
    });
  }

  function needsStorefrontFetch(config) {
    return (
      config.variantsCount > 250 ||
      (config.embeddedVariantCount >= 250 &&
        config.variantsCount > config.embeddedVariantCount)
    );
  }

  function isFullMatrix(variantCount, matrixSize) {
    return matrixSize > 0 && variantCount === matrixSize;
  }

  function init() {
    var config = window.pdpVariantCascadeConfig;
    if (!config || !config.optionNames || config.optionNames.length < 2) {
      return;
    }

    var variantSelects = document.querySelector("variant-selects");
    if (!variantSelects) return;

    var shouldFetch = needsStorefrontFetch(config);
    var embedded = parseEmbeddedVariants(variantSelects);

    if (!shouldFetch && isFullMatrix(config.variantsCount, config.matrixSize)) {
      return;
    }

    var loadPromise;
    if (shouldFetch) {
      setLoading(variantSelects, true);
      loadPromise = fetchAllVariantsFromStorefront(config).then(function (
        fetched
      ) {
        setLoading(variantSelects, false);
        if (fetched && fetched.length) return fetched;
        console.warn(
          "[variant-cascade] Falling back to embedded variant JSON (" +
            embedded.length +
            " variants)."
        );
        return embedded;
      });
    } else {
      loadPromise = Promise.resolve(embedded);
    }

    loadPromise.then(function (variants) {
      if (!variants || !variants.length) return;

      variants = normalizeVariantOptions(
        variants,
        config.optionNames.length
      );

      var fullMatrix = isFullMatrix(variants.length, config.matrixSize);

      if (!shouldFetch && fullMatrix) {
        return;
      }

      replaceVariantData(variantSelects, variants);

      hookSelectedLabelStyles(variantSelects);
      normalizeSelectedLabelStyles(variantSelects);

      if (!fullMatrix) {
        activateCascading(variantSelects, variants, config);
      } else {
        variantSelects
          .querySelectorAll("[data-size-dropdown-menu] label")
          .forEach(function (label) {
            label.classList.add("text-black");
            label.classList.remove("text-gray-400", "ss-disabled");
          });
      }

      if (typeof variantSelects.onVariantChange === "function") {
        variantSelects.onVariantChange.call(variantSelects);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
